import Accordion from "components/shared/accordion/Accordion";
import GuiContext from "config/context/guiContext";
import TableContext from "config/context/tableContext";
import ThemeContext from "config/context/themeContext";
import styles from "css/dashboard/dashboard.module.css";
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../shared/button/Button";
import Option from "../shared/select-and-option/Option";
import Select from "../shared/select-and-option/Select";
import AddNewAccount from "./AddNewAccount";
import ShareAccess from "./ShareAccess";
import AddNewServer from "./AddNewServer";

export default function Dashboard() {
  const { dark } = useContext(ThemeContext);
  const { userData, selectedAccount, selectedServer, selected } =
    useContext(GuiContext);
  const { emptyTable } = useContext(TableContext);
  const [openAddAccountModal, setOpenAddAccountModal] = useState(false);
  const [openRemoveAccountModal, setOpenRemoveAccountModal] = useState(false);
  const [openDetachModal, setOpenDetachModal] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [openAddServerModal, setOpenAddServerModal] = useState(false);
  const [openRemoveServerModal, setOpenRemoveServerModal] = useState(false);

  // Matična baza (prva u spisku) je nalog kojim je korisnik registrovan i uvek
  // ostaje: ne skida se sa spiska niti predaje. Brana je i na backendu — ovo je
  // da korisnik vidi razlog pre klika, a ne grešku posle. Predaja se sprečava u
  // dijalogu za deljenje (ShareAccess), gde handover i živi.
  const isPrimarySelected =
    Boolean(selected.selectedAccount) &&
    userData?.accounts?.[0] === selected.selectedAccount;

  // Brisanje sadržaja je uvek dozvoljeno — prazna baza u spisku je normalno
  // stanje, tako svaki nalog i počinje. Brana je premeštena na SKIDANJE SA
  // SPISKA, u sam dijalog, jer je to jedina radnja koja spisak može isprazniti.
  const handleDeleteAccount = () => {
    setOpenRemoveAccountModal(true);
  };

  // Provera je bila i ovde i na samom dugmetu, pa se poruka nije mogla dostići:
  // sa jednim serverom dugme je onemogućeno i ne da se kliknuti, a sa dva
  // provera prođe. Razlog sada nosi dugme, gde ga korisnik i traži.
  const handleDeleteServer = () => {
    setOpenRemoveServerModal(true);
  };

  const handleSelectedAccount = async (e) => {
    let myTableName = e.target.value;
    if (!myTableName) return;
    emptyTable();
    selectedAccount(myTableName);
  };

  return (
    <section
      className={`${styles.dashboard} ${dark ? styles.dark : ""
        } column justify-start`}
    >
      <div className={styles.dashboard_header}>
        <h2>Dashboard</h2>
        <p>
          Welcome to your dashboard. Here's a quick overview of your databases
          and servers.
        </p>
      </div>
      <div id={styles.controlGroup}>
        <Accordion header={<h3>Databases</h3>} isOpen={true}>
          <p>
            <img src="./img/info.svg" alt="info" width="16" height="16" />
            &nbsp;
            <i>
              Manage your databases with ease. Click to access, add, or delete.
            </i>
          </p>
          <div className="container">
            <label htmlFor="accountSelect">Database Name</label>
            <Select
              value={selected.selectedAccount}
              onChange={handleSelectedAccount}
              className={styles.select}
              name="accountSelect"
              id="accountSelect"
            >
              {userData &&
                userData.accounts &&
                userData.accounts.map((account, i) => (
                  <Option key={account + i} value={account}>
                    {account}
                  </Option>
                ))}
            </Select>
          </div>
          {/* Pokazivač, ne sadržaj. `Dashboard` je mesto gde se pristup MENJA,
              `Permissions` gde se čita — a do 01.09. se čitalo tako što se
              krene u skidanje sa spiska, dakle kroz dijalog koji preti. */}
          {selected.selectedAccount && (
            <p className={styles.accessHint}>
              <Link to="/permissions">Who reaches this database →</Link>
            </p>
          )}
          <div className="column align-center gap-05">
            <Button onClick={() => setOpenAddAccountModal(true)} expand="full">
              Add New Database
            </Button>
            {/* Redosled prati TEŽINU, a ne redosled nastanka: prvo ono što
                dodaje, pa ono što menja doseg, pa na dnu jedino nepovratno.
                Ranije je „skini sa spiska" stajalo ISPOD brisanja, pa je
                bezopasna radnja bila iza razorne. */}
            <Button
              onClick={() => setOpenShareModal(true)}
              buttonType="text"
              expand="full"
            >
              Share Access…
            </Button>
            {/* Ne dira lanac — nalog, kontrakt i podaci ostaju, menja se samo
                ko bazu vidi. Zato nije obojeno kao opasno. */}
            <Button
              onClick={() => setOpenDetachModal(true)}
              buttonType="text"
              expand="full"
              disabled={isPrimarySelected}
              disabledReason={
                isPrimarySelected
                  ? "This is your main database — the account you registered with. It stays on your list, even when empty. Use Delete Database to remove its contents and its code."
                  : undefined
              }
            >
              Remove From My List
            </Button>
            <Button
              onClick={handleDeleteAccount}
              variant="danger"
              buttonType="text"
              expand="full"
            >
              Delete Database
            </Button>
          </div>
        </Accordion>
        <Accordion header={<h3>Servers</h3>} isOpen={true}>
          <p>
            <span>
              <img src="./img/info.svg" alt="info" width="16" height="16" />
            </span>
            &nbsp;
            <i>Manage your servers below. Click to access, add, or delete.</i>
          </p>
          <div className="container">
            <label htmlFor="serverURL">Server URL</label>
            <Select
              value={selected.selectedServer}
              onChange={(e) => selectedServer(e.target.value)}
              className={styles.select}
              name="serverURL"
              id="serverURL"
            >
              {userData &&
                userData.servers &&
                userData?.servers.map((server, i) => (
                  <Option key={server + i} value={server}>
                    {server}
                  </Option>
                ))}
            </Select>
          </div>
          <div className="column align-center gap-05">
            <Button onClick={() => setOpenAddServerModal(true)} expand="full">
              Add New Server
            </Button>
            <Button
              onClick={handleDeleteServer}
              variant="danger"
              buttonType="text"
              expand="full"
              disabled={userData.servers.length <= 1}
              disabledReason="This is your only server. Add another one before removing this."
            >
              Remove Server
            </Button>
          </div>
        </Accordion>
      </div>
      <AddNewAccount
        typeCall={"link_database"}
        setOpenModal={setOpenAddAccountModal}
        openModal={openAddAccountModal}
      />
      <AddNewAccount
        typeCall={"unlink_database"}
        setOpenModal={setOpenRemoveAccountModal}
        openModal={openRemoveAccountModal}
      />
      <AddNewAccount
        typeCall={"detach_database"}
        setOpenModal={setOpenDetachModal}
        openModal={openDetachModal}
      />
      <ShareAccess setOpenModal={setOpenShareModal} openModal={openShareModal} />
      <AddNewServer
        typeCall={"add_server"}
        setOpenModal={setOpenAddServerModal}
        openModal={openAddServerModal}
      />
      <AddNewServer
        typeCall={"remove_server"}
        setOpenModal={setOpenRemoveServerModal}
        openModal={openRemoveServerModal}
      />
    </section>
  );
}
